import React, { useContext, useState, useEffect } from 'react';
import useGetFeatureAccess from 'hooks/useGetUserAuthData';
import {
    AccessLevels, IFeatureAccess, IFeatureAccessMap, TAccessLevel, TFeatureId, TUserClassId, IRole,
} from 'types/auth';
import { IAuthenticatedUser, User } from 'queries/UserMenu/useGetAuthenticatedUser';
import { USER_CLASSES, getUserSpecificSelectedOrgKey } from 'services/constants';
import useGetUserOrganizations, { IUserOrganization } from 'hooks/useGetUserOrganizations';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import { getCookie, setCrossDomainCookie } from 'helpers/localStorage/cookies';

export interface IAuthContext {
    /**
     * selectedOrganizationId: correlation ID of the selected organization.
     */
    selectedOrganizationId?: string;

    /**
     * handleUpdateSelectedOrganization: function to update the selected organization by int ID.
     * @param orgId number - The int ID of the organization to select.
     */
    handleUpdateSelectedOrganization: (orgId: number) => void;
    userOrganizations: IUserOrganization[];
    isLoadingUserOrganizations: boolean;
    orgPermissions: IFeatureAccessMap;
    isLoadingAuthData: boolean;
    isFeatureAccessibleToUser: (featureIds: TFeatureId[], requiredAccessLevel: TAccessLevel) => boolean;
    role: IRole | null;
    user: User | null;
}

const defaultAuthContext: IAuthContext = {
    selectedOrganizationId: undefined,
    handleUpdateSelectedOrganization: () => {},
    userOrganizations: [],
    isLoadingUserOrganizations: true,
    orgPermissions: {},
    isLoadingAuthData: true,
    isFeatureAccessibleToUser: () => false,
    role: null,
    user: null,
};

export const AuthContext = React.createContext<IAuthContext>(defaultAuthContext);

export const useAuthContext = () => useContext(AuthContext);
export default useAuthContext;

export const featureArrayToObject = (features: IFeatureAccess[] | undefined): IFeatureAccessMap => {
    if (!features) return {};
    return features.reduce((featuresObj, feature) => ({
        ...featuresObj,
        [feature.id]: feature,
    }), {} as IFeatureAccessMap);
};

export const isFeatureAccessible = (
    featureIds: number[],
    requiredAccessLevel: TAccessLevel,
    orgPermissions: IFeatureAccessMap,
    userClass: TUserClassId | undefined,
) => {
    if (userClass === USER_CLASSES.SUPER_ADMIN) return true;
    return featureIds.some(featureId => {
        const accessLevel = orgPermissions?.[featureId]?.accessLevel ?? AccessLevels.NO_ACCESS;
        return accessLevel >= requiredAccessLevel;
    });
};

export const AuthProvider = ({
    children,
    authenticatedUser,
}: {
    children: React.ReactNode;
    authenticatedUser: IAuthenticatedUser
}) => {
    const userSpecificSelectedOrgKey = getUserSpecificSelectedOrgKey(authenticatedUser.user.id);

    const getCorrelationIdFromId = (orgId: number | undefined): string => {
        if (orgId === authenticatedUser.primaryOrganization.organizationId) {
            return authenticatedUser.primaryOrganization.organizationCorrelationId;
        }
        const match = orgId ? authenticatedUser.secondaryOrganizations.find(org => orgId === org.organizationId) : undefined;
        if (!match) {
            // if the org ID from the cookie is not provided, or if it does not match any of the
            // user's organizations, default to the primary organization and update the cookie
            const primaryOrg = authenticatedUser.primaryOrganization;
            setCrossDomainCookie(userSpecificSelectedOrgKey, String(primaryOrg.organizationId), 34560000);
            return primaryOrg.organizationCorrelationId;
        }
        return match.organizationCorrelationId;
    };

    const getInitialSelectedOrganizationId = () => getCorrelationIdFromId(Number(getCookie(userSpecificSelectedOrgKey)));

    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(getInitialSelectedOrganizationId);

    const handleUpdateSelectedOrganization = (orgId: number) => {
        setCrossDomainCookie(userSpecificSelectedOrgKey, String(orgId), 34560000);
        setSelectedOrganizationId(getCorrelationIdFromId(orgId));
    };

    const { data: userAuthData, isLoading: isLoadingUserAuthData } = useGetFeatureAccess(selectedOrganizationId!, {
        enabled: Boolean(selectedOrganizationId),
        staleTime: Infinity,
    });

    const orgPermissions = featureArrayToObject(userAuthData?.featureAccess) || {};

    const { setIdentity } = useTrackingCode();

    const isFeatureAccessibleToUser = (
        featureIds: TFeatureId[], requiredAccessLevel: TAccessLevel,
    ) => isFeatureAccessible(featureIds, requiredAccessLevel, orgPermissions, authenticatedUser.user.userClass);

    const { data: userOrganizations = [], isLoading: isLoadingUserOrganizations } = useGetUserOrganizations();

    useEffect(() => {
        if (
            !isLoadingUserOrganizations
            && authenticatedUser
        ) {
            setIdentity(authenticatedUser.user.email, { id: authenticatedUser.user.id });
            if ((window as any).heap) {
                (window as any).heap.identify(authenticatedUser.user.email);
                (window as any).heap.addUserProperties({
                    email: authenticatedUser.user.email,
                    name: authenticatedUser.user.name,
                    user_class: authenticatedUser.user.userClass,
                    organization_id: userOrganizations.map(org => org.organization.id).join(', '),
                    organization_name: userOrganizations.map(org => org.organization.name).join(', '),
                });
            }
        }
    }, [authenticatedUser, userOrganizations, isLoadingUserOrganizations]);

    return (
        <AuthContext.Provider
            value={{
                selectedOrganizationId: selectedOrganizationId || undefined,
                handleUpdateSelectedOrganization,
                userOrganizations,
                isLoadingUserOrganizations,
                orgPermissions,
                isLoadingAuthData: isLoadingUserAuthData,
                isFeatureAccessibleToUser,
                role: userAuthData?.role || null,
                user: authenticatedUser.user || null,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
